import { useEffect } from "react";
import { Outlet, NavLink, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  VStack,
  Icon,
  Divider,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { HiOutlineViewGrid } from "react-icons/hi";
import { useGetProfileQuery } from "../../../redux/reducers/apiSlice";
import { ProfileAvatar } from "../../Components/ProfileAvatar/ProfileAvatar";
import useSignout from "../../hooks/useSignout";
import { vars } from "../../Theme.css";
import { platformScrollbarSx } from "../../utils/platformScrollbar";

const PAGE_BG = "#f4f7fb";
const SIDEBAR_WIDTH = "15rem";
const SIDEBAR_SHADOW = "4px 0 32px -16px rgba(15, 23, 42, 0.12)";

/**
 * Measure platform ops shell: fixed left rail on desktop, single scroll region for content.
 */
const MeasureInternalLayout = () => {
  const { data: profile } = useGetProfileQuery();
  const { signOut } = useSignout();
  const displayName = profile?.name
    ? `${profile.name.firstName} ${profile.name.lastName}`.trim()
    : "Operator";

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootOverflow = root?.style.overflow ?? "";
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.overflow = "hidden";
      root.style.height = "100dvh";
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.overflow = prevRootOverflow;
        root.style.height = prevRootHeight;
      }
    };
  }, []);

  return (
    <Flex
      direction="column"
      h="100dvh"
      maxH="100dvh"
      overflow="hidden"
      bg={PAGE_BG}
      fontFamily={vars.fonts.body}
    >
      <Flex
        display={{ base: "flex", md: "none" }}
        align="center"
        justify="space-between"
        px={3}
        py={2}
        bg="white"
        borderBottom="1px solid"
        borderColor="blackAlpha.100"
        flexShrink={0}
      >
        <RouterLink to="/measure/internal">
          <Text fontWeight="semibold" fontSize="sm" color="blue.800">
            Measure · Ops
          </Text>
        </RouterLink>
      </Flex>

      <Flex flex={1} minH={0} align="stretch" position="relative" w="100%" overflow="hidden">
        <Flex
          as="nav"
          display={{ base: "none", md: "flex" }}
          direction="column"
          position="fixed"
          left={0}
          top={0}
          zIndex={20}
          w={SIDEBAR_WIDTH}
          h="100vh"
          bg="white"
          borderRight="1px solid"
          borderColor="blackAlpha.100"
          boxShadow={SIDEBAR_SHADOW}
          py={3}
          px={2.5}
          sx={{
            scrollbarWidth: "thin",
            scrollbarColor: "var(--chakra-colors-blue-200) transparent",
          }}
        >
          <RouterLink to="/measure/internal">
            <HStack spacing={2} px={1.5} py={1.5} mb={4}>
              <Box as="img" src="/images/logo-5.png" alt="" h="22px" w="auto" />
              <Text fontSize="11px" fontWeight="bold" letterSpacing="0.08em" color="blue.900">
                MEASURE
              </Text>
            </HStack>
          </RouterLink>

          <Text fontSize="10px" fontWeight="semibold" color="gray.400" letterSpacing="0.06em" px={2} mb={2}>
            WORKSPACE
          </Text>

          <VStack align="stretch" spacing={0.5} flex="1" minH={0}>
            <NavLink to="/measure/internal" end>
              {({ isActive }) => (
                <HStack
                  spacing={2.5}
                  px={2.5}
                  py={2}
                  borderRadius="md"
                  bg={isActive ? "blue.50" : "transparent"}
                  color={isActive ? "blue.800" : "gray.600"}
                  fontWeight={isActive ? "semibold" : "medium"}
                  fontSize="sm"
                  transition="background 0.12s ease, color 0.12s ease"
                  _hover={{ bg: isActive ? "blue.50" : "gray.50", color: "gray.800" }}
                >
                  <Icon as={HiOutlineViewGrid} boxSize={4} opacity={isActive ? 1 : 0.85} />
                  <Text>Overview</Text>
                </HStack>
              )}
            </NavLink>
          </VStack>

          <Divider borderColor="blackAlpha.100" my={3} />

          <HStack spacing={2} px={1.5} py={2} align="center" borderRadius="md" bg="gray.50">
            <ProfileAvatar
              size="sm"
              src={profile?.image?.url}
              name={displayName}
              showPaidRing={profile?.subscription_tier === "PAID"}
            />
            <Box minW={0} flex={1}>
              <Text fontSize="xs" fontWeight="semibold" noOfLines={1} color="gray.800">
                {displayName}
              </Text>
              <Text fontSize="10px" color="gray.500" noOfLines={1}>
                {profile?.email}
              </Text>
            </Box>
          </HStack>
          <Button
            mt={2}
            size="sm"
            variant="outline"
            colorScheme="blue"
            borderRadius="md"
            onClick={() => signOut()}
          >
            Sign out
          </Button>
        </Flex>

        <Flex
          direction="column"
          flex="1"
          minW={0}
          minH={0}
          h="full"
          ml={{ base: 0, md: SIDEBAR_WIDTH }}
          bg={PAGE_BG}
          overflow="hidden"
        >
          <Box
            as="header"
            px={{ base: 3, md: 5 }}
            py={2.5}
            bg="white/90"
            backdropFilter="blur(8px)"
            borderBottom="1px solid"
            borderColor="blackAlpha.100"
            flexShrink={0}
          >
            <Flex align="center" justify="space-between" gap={3}>
              <HStack spacing={1} color="gray.500" fontSize="sm" flexWrap="wrap">
                <Text fontWeight="medium">Measure</Text>
                <Icon as={ChevronRightIcon} color="gray.300" boxSize={3} />
                <Text color="gray.800" fontWeight="semibold">
                  Overview
                </Text>
              </HStack>
            </Flex>
          </Box>

          <Box
            flex={1}
            minH={0}
            overflowY="auto"
            overflowX="hidden"
            px={{ base: 3, md: 5 }}
            py={{ base: 4, md: 4 }}
            sx={platformScrollbarSx}
          >
            <Outlet />
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default MeasureInternalLayout;
